import { useMutation, useQueries, useQuery, type QueryClient } from "@tanstack/react-query";
import { joinMinistry, leaveMinistry } from "@/api/authorization";
import { getMemberMembershipPeriods, getMembershipContexts } from "@/api/ministryMemberships";

// Membership changes affect both current rosters and historical eligibility.
// Invalidate all reads keyed to this church, including member/household detail,
// occurrence rosters, reports, Intelligence, Care and the ministry overview.
export function invalidateMembershipQueries(client: QueryClient, churchId: string) {
  return client.invalidateQueries({ predicate: query => query.queryKey.includes(churchId) });
}

export function useMemberMemberships(churchId: string, memberId: string) {
  const contexts = useQuery({
    queryKey: ["membership-contexts", churchId],
    queryFn: () => getMembershipContexts(churchId),
  });
  const visible = contexts.isError ? [] : (contexts.data ?? []).filter(context => context.can_view);
  const periods = useQueries({
    queries: visible.map(context => ({
      queryKey: ["ministry-memberships", churchId, memberId, context.ministry_id],
      queryFn: ({ signal }: { signal: AbortSignal }) => getMemberMembershipPeriods(churchId, context.ministry_id, memberId, signal),
    })),
  });
  return { contexts, groups: visible.map((context, index) => ({ context, query: periods[index] })) };
}

type MembershipChange =
  | { action: "join"; ministryId: string; memberId: string; effectiveAt: string }
  | { action: "leave"; ministryId: string; membershipId: string; effectiveAt: string };

export function useMembershipMutation(client: QueryClient, churchId: string) {
  return useMutation({
    mutationFn: (change: MembershipChange) => change.action === "join"
      ? joinMinistry(churchId, change.ministryId, change.memberId, change.effectiveAt)
      : leaveMinistry(churchId, change.ministryId, change.membershipId, change.effectiveAt),
    onSuccess: () => invalidateMembershipQueries(client, churchId),
  });
}
